import html2canvas from 'html2canvas';
import { DownloadCloudIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { Button, Loader, Modal } from '@/components';
import { IconShare } from '@/components/Icons/IconShare';
import { Paginator } from '@/components/Paginator';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { getAwards } from '@/services/editAccount';
import { ResponseMyAwards } from '@/types/teamf5';
import { COLORS } from '@/utils';

type AwardsMockType = Record<
  'top10k' | 'top25k' | 'top100' | 'top3' | 'top2' | 'winner',
  { date: string; label: string }
>;

const typeColors: Record<keyof AwardsMockType, { icon: string; bg: string }> = {
  top25k: { icon: 'text-gdt-gray-4', bg: 'bg-gdt-gray-6' },
  top10k: { icon: 'text-gdt-status-orange', bg: 'bg-gdt-status-orange-light' },
  top100: { icon: 'text-gdt-status-green', bg: 'bg-gdt-status-green-light' },
  top3: { icon: 'stroke-gdt-blue', bg: 'bg-gdt-gray-6' },
  top2: { icon: 'stroke-gdt-blue', bg: 'bg-gdt-blue-light2' },
  winner: {
    icon: 'stroke-gdt-status-yellow',
    bg: 'bg-gdt-status-yellow-light',
  },
};

const AwardIdTable: Record<number, keyof AwardsMockType> = {
  17: 'top25k',
  3: 'top10k',
  5: 'top100',
  6: 'winner',
  7: 'winner',
  9: 'winner',
};

interface AwardProps {
  type: keyof AwardsMockType;
}

const IcAward = ({ type }: AwardProps) => (
  <svg
    className={`w-5 h-5 fill-current   ${typeColors[type].icon}`}
    fill="none"
    height="17"
    viewBox="0 0 17 17"
    width="17"
  >
    <path
      clipRule="evenodd"
      d="M8.50001 1.41667C6.1528 1.41667 4.25001 3.31946 4.25001 5.66667C4.25001 8.01388 6.1528 9.91667 8.50001 9.91667C10.8472 9.91667 12.75 8.01388 12.75 5.66667C12.75 3.31946 10.8472 1.41667 8.50001 1.41667ZM2.83334 5.66667C2.83334 2.53705 5.3704 0 8.50001 0C11.6296 0 14.1667 2.53705 14.1667 5.66667C14.1667 8.79628 11.6296 11.3333 8.50001 11.3333C5.3704 11.3333 2.83334 8.79628 2.83334 5.66667Z"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M11.0914 9.12928C11.4792 9.07783 11.8353 9.3505 11.8867 9.7383L12.7438 16.1983C12.7796 16.4679 12.6578 16.7342 12.4305 16.8835C12.2032 17.0328 11.9104 17.0388 11.6772 16.8989L8.49999 14.9925L5.32275 16.8989C5.08952 17.0388 4.79675 17.0328 4.56942 16.8835C4.34208 16.7341 4.22034 16.4678 4.25615 16.1982L5.11324 9.74528C5.16474 9.35749 5.52087 9.08487 5.90867 9.13638C6.29646 9.18789 6.56908 9.54401 6.51757 9.93181L5.85397 14.928L8.13555 13.5591C8.35987 13.4245 8.6401 13.4245 8.86442 13.5591L11.1462 14.9282L10.4824 9.92462C10.4309 9.53682 10.7036 9.18073 11.0914 9.12928Z"
      fillRule="evenodd"
    />
  </svg>
);

const IcCup = ({ type }: AwardProps) => (
  <svg
    className={`${typeColors[type].icon}`}
    fill="none"
    height="16"
    width="16"
  >
    <path
      d="M8.102 11v1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4.766 14.668h6.666v-.667c0-.733-.6-1.333-1.333-1.333h-4c-.733 0-1.333.6-1.333 1.333v.667Z"
      strokeMiterlimit="10"
      strokeWidth="1.5"
    />
    <path
      d="M4.102 14.666h8M7.999 10.667a4.663 4.663 0 0 1-4.667-4.666V4a2.666 2.666 0 0 1 2.667-2.667h4a2.666 2.666 0 0 1 2.666 2.667v2A4.663 4.663 0 0 1 8 10.667Z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M3.646 7.767a3.24 3.24 0 0 1-1.287-.8c-.6-.667-1-1.467-1-2.4A1.65 1.65 0 0 1 3.026 2.9h.433c-.133.307-.2.647-.2 1v2c0 .667.14 1.294.387 1.867ZM12.352 7.767c.5-.16.94-.453 1.286-.8.6-.667 1-1.467 1-2.4A1.65 1.65 0 0 0 12.972 2.9h-.434c.134.307.2.647.2 1v2a4.7 4.7 0 0 1-.386 1.867Z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

interface AwardsMapped {
  id: number;
  type: keyof AwardsMockType;
  date: string;
  label: string;
  score: number;
  position: number;
}

const ITEM_PER_PAGE = 5;

export const MyAwards = () => {
  const [currentItems, setCurrentItems] = useState<AwardsMapped[]>([]);
  const { isMobile } = useIsDesktop();
  /**
   * UX STATES
   */
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFile, setGeneratedFile] = useState<File | null>(null);

  const [diploma, setDiploma] = useState<AwardsMapped | null>(null);
  const toast = useToast();
  const diplomaRef = useRef(null);
  const { userInitialData: user } = useAuth();

  const mapSorter = (data: ResponseMyAwards) => {
    if (data?.reconocimientos) {
      return (
        data.reconocimientos
          .reduce((acc, award) => {
            const type = AwardIdTable[award.idReconocimiento];
            if (type) {
              acc.push({
                id: award.id,
                type,
                date: award.fecha,
                label: award.textoLink,
                position: award.posicion,
                score: award.puntos,
              });
            }
            return acc;
          }, [] as AwardsMapped[])
          .sort((a, b) =>
            b?.date > a?.date ? 1 : b?.date < a?.date ? -1 : 0
          ) || []
      );
    }
    return [];
  };

  const { data, isLoading, isFetching } = useQuery(
    'getAwards',
    async () => getAwards(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      onSuccess: (data) => {
        if (data?.reconocimientos && data.estado) {
          const mapped = mapSorter(data);
          if (JSON.stringify(mapped) !== JSON.stringify(currentItems)) {
            setCurrentItems(mapped.slice(0, ITEM_PER_PAGE));
          }
        } else {
          toast.show(data?.mensaje);
        }
      },
      onError: (err) => {
        const error = err as Error;
        toast.show(error.message);
      },
    }
  );

  const dataMapped: AwardsMapped[] = useMemo(() => {
    if (!data?.reconocimientos) return [];
    return mapSorter(data);
  }, [data]);

  /**
   * =========================================
   * PREGENERAR IMAGEN AL ABRIR MODAL
   * =========================================
   */
  useEffect(() => {
    if (!diploma?.id) {
      setDiploma(null);
      setGeneratedFile(null);
      return;
    }

    let cancelled = false;

    const generateDiploma = async () => {
      try {
        setIsGenerating(true);

        /**
         * Esperar a que React pinte
         */
        await new Promise(requestAnimationFrame);

        /**
         * Pequeño delay para asegurar fonts/images
         */
        await new Promise((resolve) => setTimeout(resolve, 150));

        if (!diplomaRef.current) return;

        /**
         * OPTIMIZACIONES:
         * - scale reducido
         * - logging desactivado
         * - background null
         */
        const canvas = await html2canvas(diplomaRef.current, {
          useCORS: true,
          backgroundColor: null,
          logging: false,
          scale: window.devicePixelRatio > 1 ? 1.5 : 1,
        });

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/png', 0.92)
        );

        if (!blob || cancelled) return;

        const file = new File([blob], 'diploma-grandt.png', {
          type: 'image/png',
        });

        setGeneratedFile(file);
      } catch (err) {
        console.error(err);
        toast.show('Error generando diploma');
      } finally {
        if (!cancelled) {
          setIsGenerating(false);
        }
      }
    };

    generateDiploma();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diploma?.id]);

  const handlePageChange = (items: AwardsMapped[]) => {
    dataMapped && setCurrentItems(items);
  };

  /**
   * =========================================
   * SHARE
   * =========================================
   */
  const handleShare = useCallback(async () => {
    try {
      if (!generatedFile) {
        toast.show('Todavía estamos preparando tu diploma...');
        return;
      }

      const message = `¡Mirá el diploma por mi buen rendimiento en Gran DT! `;

      /**
       * =========================================
       * SHARE NATIVO (mobile)
       * =========================================
       */
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [generatedFile] }) &&
        isMobile
      ) {
        try {
          await navigator.share({
            title: 'Diploma Gran DT',
            text: message,
            files: [generatedFile],
          });
        } catch (err) {
          console.error('Share cancelado o falló', err);
        }
      } else {
        /**
         * FALLBACK DESKTOP / WEBVIEW
         */
        const url = URL.createObjectURL(generatedFile);

        const a = document.createElement('a');
        a.href = url;
        a.download = generatedFile.name;

        document.body.appendChild(a);

        a.click();

        a.remove();

        URL.revokeObjectURL(url);

        toast.show(
          isMobile
            ? 'Diploma descargado para compartir.'
            : 'Diploma descargado.'
        );
      }
    } catch (err) {
      console.error(err);

      /**
       * Usuario canceló share
       */
      if ((err as Error)?.name === 'AbortError') {
        return;
      }

      toast.show('Error al compartir diploma');
    }
  }, [generatedFile, isMobile, toast]);

  return (
    <>
      <Loader isVisible={isLoading || isFetching} />
      <div className="flex flex-col gap-y-6 lg:ml-[115px] lg:mt-7 lg:pr-[40px]">
        <h2 className="text-xl font-bold max-lg:hidden">Mis diplomas</h2>
        <div className="rounded-lg lg:shadow-[0px_16px_19px_0px_rgba(0,_0,_0,_0.1)] lg:max-w-[650px] bg-white">
          <div className="flex flex-col">
            {currentItems.length === 0 && (
              <div className="flex justify-center items-center p-10">
                <span className="text-gdt-gray-3 text-lg font-bold">
                  TODAVÍA NO OBTUVISTE NINGÚN DIPLOMA
                </span>
              </div>
            )}
            {currentItems.length > 0 &&
              currentItems.map((award) => (
                <div
                  key={award.id}
                  className="flex gap-x-4 justify-between p-4 lg:px-6 items-center border-b border-gdt-gray-5 hover:bg-gdt-gray-7 cursor-pointer"
                  onClick={() => setDiploma(award)}
                >
                  <div className="flex gap-x-4 items-center col-span-8">
                    <div
                      className={`rounded-full p-3 ${
                        typeColors[award.type].bg
                      }`}
                    >
                      {['winner', 'top3', 'top2'].includes(award.type) ? (
                        <IcCup type={award.type} />
                      ) : (
                        <IcAward type={award.type} />
                      )}
                    </div>
                    <div className="flex flex-col lg:w-80">
                      <span className="text-[13px] lg:text-sm font-bold max-w-80">
                        {award.label}
                      </span>
                      <span className="text-gdt-gray-3 text-[13px] lg:text-sm lg:hidden">
                        {award.date}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-x-20 ">
                    <span className="text-gdt-gray-3 text-[13px] lg:text-sm max-lg:hidden">
                      {award.date}
                    </span>
                    <div className="p-1 rounded-md bg-gdt-blue-light2 ">
                      <figure className="h-4 min-w-4 max-w-4 lg:max-w-7">
                        {isMobile ? (
                          <IconShare />
                        ) : (
                          <DownloadCloudIcon
                            className="bottom-[1.8px] relative"
                            color={`${COLORS['gdt-blue'].DEFAULT}`}
                            size={20}
                          />
                        )}
                      </figure>
                    </div>
                  </div>
                </div>
              ))}
            {dataMapped.length > 5 && (
              <div className="max-lg:hidden">
                <Paginator
                  currentContentPage={handlePageChange}
                  items={dataMapped}
                  itemsPerPage={isMobile ? dataMapped.length : ITEM_PER_PAGE}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        bottomSheetClassName="p-0"
        className="p-0"
        closeOutside={true}
        controlledOnClose={() => {
          setDiploma(null);
          setGeneratedFile(null);
        }}
        headerSheetClassName="p-0 absolute z-10"
        isControlledOpen={Boolean(diploma)}
        render={() => {
          return (
            <div className="lg:w-[585px] lg:h-[415px] w-full h-[320px] lg:max-w-none max-w-[450px] mx-auto relative">
              {/* DIPLOMA */}
              <div
                ref={diplomaRef}
                className="lg:w-[585px] lg:h-[415px] lg:bg-cover lg:bg-center bg-[auto_100%] bg-[55%_top] relative bg-[url('/assets/images/diploma.jpg')] w-full h-[320px] lg:max-w-none max-w-[450px] mx-auto"
              >
                <div className="absolute w-full h-full text-center">
                  <div className="text-center lg:mt-[135px] mt-[100px]">
                    {/* Nombre */}
                    <div className="lg:text-lg text-base text-gdt-blue-dark font-extrabold">
                      <p>
                        ¡FELICITACIONES <br />
                        <span className="text-gdt-blue uppercase font-extrabold">
                          {user?.nombre_dt}
                        </span>
                        !
                      </p>
                    </div>

                    {/* Datos */}
                    <div className="lg:text-[17px] text-sm lg:mt-2 mt-1">
                      <p>
                        Con{' '}
                        <b className="text-gdt-blue">{diploma?.score} puntos</b>{' '}
                        lograste posicionar a tu equipo
                        <br />
                        <b className="text-gdt-blue">
                          &quot;{user?.nombre_equipo}&quot;
                        </b>
                        <br /> en el{' '}
                        <b className="text-gdt-blue">
                          puesto {diploma?.position}
                        </b>{' '}
                        en la <b className="text-gdt-blue">{diploma?.date}.</b>
                      </p>
                    </div>
                  </div>
                  {/* BOTÓN */}
                </div>
              </div>
              <Button
                className="flex justify-center items-center space-x-3 h-9 mx-auto lg:mt-4 mt-2 w-auto absolute lg:bottom-[115px] bottom-[85px] left-1/2 -translate-x-1/2"
                disabled={isGenerating}
                type="button"
                variant="primary"
                onClick={handleShare}
              >
                <figure className="max-w-5">
                  {isMobile ? (
                    <IconShare color="white" />
                  ) : (
                    <DownloadCloudIcon />
                  )}
                </figure>
                <p className="uppercase flex-1">
                  {isGenerating
                    ? 'Preparando diploma...'
                    : isMobile
                    ? 'Compartir'
                    : 'Descargar'}
                </p>
              </Button>
            </div>
          );
        }}
        size="auto"
        variant="controlled"
      />
    </>
  );
};
